import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useUi } from "@/stores/ui";
import { useCouponCart } from "@/stores/couponCart";
import { useActiveSubscription } from "@/hooks/useSubscription";
import { COUPON_TYPES, PLANS, type CouponType, type PlanId } from "@/lib/constants";
import { Crown, Lock, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Modal "Nouveau coupon" : on liste les 3 types de coupon (TOP10/TOP20/TOP30)
 * et on lock chacun selon le plan actif de l'utilisateur.
 *   - Starter         → TOP30 dispo, TOP20/TOP10 lockés
 *   - Standard        → TOP30/TOP20 dispos, TOP10 locké
 *   - Premium/BigBoss → tous dispos
 *   - Pas d'abonnement → bannière "Active un plan", tout locké
 */
const ORDER: CouponType[] = ["TOP10", "TOP20", "TOP30"];

const META: Record<CouponType, { title: string; subtitle: string }> = {
  TOP10: { title: "10 matchs · le plus rare", subtitle: "Pour les pros. Gain max selon plan." },
  TOP20: { title: "20 matchs · équilibré", subtitle: "Le bon compromis difficulté / accessibilité." },
  TOP30: { title: "30 matchs · plus accessible", subtitle: "Idéal pour démarrer." },
};

const PLAN_RANK: Record<PlanId, number> = {
  starter: 1,
  standard: 2,
  premium: 3,
  bigboss: 4,
};

function planRank(plan: string): number {
  return PLAN_RANK[plan as PlanId] ?? 0;
}

export function CouponTypePicker() {
  const { isCouponSheetOpen, setCouponSheetOpen } = useUi();
  const { setType } = useCouponCart();
  const sub = useActiveSubscription();
  const activePlan = sub.data?.plan as PlanId | undefined;

  function pick(type: CouponType) {
    setType(type);
    setCouponSheetOpen(false);
  }

  return (
    <Dialog open={isCouponSheetOpen} onOpenChange={setCouponSheetOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nouveau coupon</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Choisis ton type de coupon. Plus le défi est dur, plus le gain est élevé.
          </p>
        </DialogHeader>

        {/* Banner si aucun abonnement actif */}
        {!sub.data && (
          <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/[0.06] px-3.5 py-3">
            <Crown className="size-4 text-primary mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold">Abonnement requis</p>
              <p className="text-2xs text-muted-foreground mt-0.5 leading-relaxed">
                Active un plan pour soumettre des coupons quotidiens. Dès 1 000 FCFA / mois.
              </p>
            </div>
            <Button asChild size="sm" onClick={() => setCouponSheetOpen(false)}>
              <Link to="/plans">Plans</Link>
            </Button>
          </div>
        )}

        <div className="space-y-2 mt-1">
          {ORDER.map((type) => {
            const info = COUPON_TYPES[type];
            const requiredPlan = PLANS[info.requiredPlan as PlanId];
            const canUse = activePlan
              ? planRank(activePlan) >= planRank(info.requiredPlan)
              : false;
            return (
              <button
                key={type}
                disabled={!canUse}
                onClick={() => pick(type)}
                className={cn(
                  "w-full flex items-center gap-3 rounded-xl border bg-card px-3.5 py-3 text-left transition-all duration-180",
                  canUse
                    ? "border-white/[0.06] hover:border-primary/40 hover:bg-white/[0.02]"
                    : "border-white/[0.04] opacity-50 cursor-not-allowed",
                )}
              >
                <div
                  className={cn(
                    "flex size-10 shrink-0 items-center justify-center rounded-lg text-xs font-bold tabular-nums",
                    type === "TOP10" && "bg-primary/10 text-primary border border-primary/20",
                    type === "TOP20" && "bg-white/[0.06] text-foreground border border-white/[0.06]",
                    type === "TOP30" && "bg-white/[0.04] text-muted-foreground border border-white/[0.08]",
                  )}
                >
                  {type.slice(3)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold tracking-tight">{type}</p>
                    {!canUse && <Lock className="size-3 text-muted-foreground" />}
                  </div>
                  <p className="text-2xs text-muted-foreground mt-0.5 truncate">
                    {META[type].title}
                  </p>
                  {!canUse && (
                    <p className="text-2xs text-warning mt-0.5">
                      Plan {requiredPlan.name}+ requis
                    </p>
                  )}
                </div>
                {canUse ? (
                  <ChevronRight className="size-4 text-muted-foreground shrink-0" />
                ) : null}
              </button>
            );
          })}
        </div>

        <Button variant="ghost" className="w-full mt-2" onClick={() => setCouponSheetOpen(false)}>
          Annuler
        </Button>
      </DialogContent>
    </Dialog>
  );
}
